/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import RtiRiskCriteriaOneDocumentList from 'components/utilized/rti/RtiRiskCriteriaOneDocumentList'

import useCrudData from "utils/customHooks/useCrudData"
import { crudColorMap, crudIconMap } from 'utils/helpers'

import {
    useDELETE_rtiRiskCriteriaOneMutation,
    useGET_rtiRiskCriteriaOneByIdQuery,
    usePOST_rtiRiskCriteriaOneMutation,
    usePUT_rtiRiskCriteriaOneMutation,
} from 'redux/api/rtiApi'

import { useGET_schoolSetupProblemAreasSelectQuery } from 'redux/api/setupApi'
import { useGET_learningStyleQuestionsQuery, useGET_studentByProfileIdQuery, useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import formModel, { initialValues} from 'utils/formModels/rtiRiskCriteriaOneModel'

import { Form, Formik } from 'formik'
import { Box, Button, Grid } from '@mui/material'
import { ActionType, IRtiRiskCriteriaOneDocument, IStudent } from 'types'
import SelectField from 'components/atoms/fields/SelectField'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import { FaChevronLeft } from 'react-icons/fa'
import StudentCard from 'components/utilized/students/studentCard'
import KpiPerformanceChart from 'components/utilized/kpi/KpiPerformanceChart'


interface IProps {
  action: ActionType
  id: string
  currentStudent?: any
  viewOnly: boolean
}


const RiskCriteriaForm: FC<IProps> = ({  action, id, currentStudent = null, viewOnly }) => {
 
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const backToSlug =  currentStudent ? `/rti-students/${currentStudent.id}#criteria ` : `/rti-risks`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('rti.identfiying-academic-risk')}`,
    entityId: id,
    getEntityQuery: useGET_rtiRiskCriteriaOneByIdQuery,
    createEntityMutation: usePOST_rtiRiskCriteriaOneMutation,
    editEntityMutation: usePUT_rtiRiskCriteriaOneMutation,
    deleteEntityMutation: useDELETE_rtiRiskCriteriaOneMutation,
  })

  const {
    student,
    problem_areas,
    parent_discussion,
    comment,
    documents,
  } = formModel()


  const serverValues = entity?.id ? entity : {}

  const selectedStudent = entity?.id 
    ? serverValues.student.title 
    : currentStudent 
      ? currentStudent.first_name+' '+currentStudent.last_name+' ['+currentStudent.academic_id+']'
      : null

  const formValues = {
    ...initialValues,
    ...serverValues
  } 


  const [optionStudent, setOptionStudent] = useState(null)
  
  const {data: studentData} = useGET_studentByProfileIdQuery(optionStudent ? optionStudent.id : null,{skip: !optionStudent})
  
  const {
    data: learningStyle,
  } = useGET_learningStyleQuestionsQuery(optionStudent ? optionStudent.id : null, {skip: !optionStudent})
        
  useEffect(() => {
     if (serverValues?.student && !optionStudent) {
      setOptionStudent(serverValues.student)
     }
   }, [serverValues?.student])

  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      onSubmit={(values: any) => {

        const data = {
          ...values,
          student_id: currentStudent ? currentStudent.student_profile_id : (values?.student?.id ?? ''),
          problem_areas_ids: values.problem_areas.map((a) => a.id),
        }

        handleSubmit(action, entity?.id, data)
    
      }}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <Form>


            {values.student && studentData && <Box mt={1} mb={3}>
               <Grid container spacing={4}>
                   <Grid item xs={12} md={4}><StudentCard student={studentData} learningStyle={learningStyle} step={1}/></Grid>
                   <Grid item xs={12} md={8}>
                       <KpiPerformanceChart filters={{ student: values.student.id }}/>
                  </Grid>
                 </Grid>
           </Box>}
 
            <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
              <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                {
                  selectedStudent && <b>{selectedStudent}</b>
                }
                {
                  !selectedStudent && <SelectField
                    fieldProps={{
                      ...student,
                    }}
                    apiCall={useGET_studentsProfileSelectQuery}
                    disabled={viewOnly}
                    onChange={(value) => {
                        setOptionStudent(value)
                    }}
                  />
                } 
              </Grid>

              <Grid item xs={12}>

                <div style={{ padding: '10px' }}>
                  {t('rti.check-problem-areas')}
                </div>

                <SelectField
                  fieldProps={{ ...problem_areas }}
                  apiCall={useGET_schoolSetupProblemAreasSelectQuery}
                  multiple={true}
                />
              </Grid>
                  

              <Grid item xs={12}>
                <RtiRiskCriteriaOneDocumentList fieldProps={{ ...documents }} disabled={false} />
              </Grid>

              <Grid item xs={12}>
                 <CheckboxField fieldProps={{ ...parent_discussion }} />
              </Grid>
              <Grid item xs={12}>
                <InputField multiline={true} fieldProps={{ ...comment }} />
              </Grid>

            </Grid>

            <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
              <Grid item xs={12}>
                
                {/* ACTIONS */}
                <Box sx={{ marginTop: '20px' }} className="transparent-background">
                  <Box sx={{ display: 'inline-block' }}>   
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(backToSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.back')}`}
                    </Button>
                  </Box> 
                  <Box sx={{ display: 'inline-block', marginLeft: '10px' }}>   
                    <SubmitButton
                              color={crudColorMap[action]}
                              text={`${t('form-actions.' + action)}`}
                              icon={crudIconMap[action]}
                              sx={{ minHeight: '2.5rem', width: '10rem' }}
                              isLoading={isLoading}
                          />
                  </Box>   
                </Box>    


              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}
export default RiskCriteriaForm
